/**
 * Contact — the target of the footer's "Contact Us" link.
 *
 * Only one contact detail on this page is real, and that constrained the design:
 *
 *   - siteConfig.contact.email is already published to Google as the Organization's
 *     customer-service contactPoint (see getOrganizationSchema in src/lib/seo.ts), so
 *     showing it here adds no new claim.
 *   - siteConfig.contact.phone is "+91-9000000000" -- a placeholder, not a number anyone
 *     answers. It is verifiably NOT rendered anywhere else on the site, and it is left
 *     unrendered here too. Publishing a dead support line on a live storefront is worse
 *     than publishing no number at all.
 *   - siteConfig.contact.address is "Mumbai, India", a city rather than an address, so it
 *     is shown as a location line and not marked up as a postal address.
 *
 * There is deliberately no contact FORM. Nothing in this app can accept one: the four
 * server handlers are Supabase Edge Functions for pricing, stock, payment verification and
 * cancellation, and none takes a message. A form that silently discarded enquiries would
 * look like working support while losing customers, so the page hands over to mail instead.
 *
 * TODO (owner): supply a real support phone number and business hours (then replace the
 * placeholder in src/config/site.ts and render it here), and a full postal address if you
 * want a LocalBusiness listing -- that needs street, city, postcode and country before the
 * schema is worth emitting.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { Mail, MapPin, Clock } from "lucide-react";
import { Reveal } from "@/components/common/reveal";
import { siteConfig } from "@/config/site";
import { getContactPageSchema, safeJsonLd } from "@/lib/seo";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: `Contact Us — ${siteConfig.name}` },
      {
        name: "description",
        content:
          "Get in touch with Aster Decorz about an order, a return, or choosing the right piece for a room. WhatsApp support, email, and customer service.",
      },
      { property: "og:title", content: `Contact Us — ${siteConfig.name}` },
      {
        property: "og:description",
        content:
          "Connect with Aster Decorz support team via WhatsApp or email for order inquiries and custom decor.",
      },
      { property: "og:url", content: `${siteConfig.url}/contact` },
    ],
    links: [{ rel: "canonical", href: `${siteConfig.url}/contact` }],
  }),
  component: Contact,
});

function Contact() {
  const contactSchema = getContactPageSchema();

  return (
    <div className="pb-28 pt-24 md:pt-28">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(contactSchema) }}
      />
      <div className="section-container">
        <Reveal>
          <p className="eyebrow">Support</p>
          <h1 className="mt-6 max-w-3xl">Get in touch</h1>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Questions about an order, a return, or which piece suits a particular corner — email is
            the fastest way to reach a person.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-12 lg:grid-cols-12">
          <div className="space-y-8 lg:col-span-6">
            {/* WhatsApp Priority Support Card */}
            <Reveal>
              <div className="border border-emerald-600/30 bg-emerald-500/5 p-7 rounded-2xl shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#25D366] text-white">
                    <svg className="h-5 w-5 fill-current" viewBox="0 0 24 24">
                      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91C2.13 13.66 2.59 15.36 3.45 16.86L2.05 22L7.3 20.62C8.75 21.41 10.38 21.83 12.04 21.83C17.5 21.83 21.95 17.38 21.95 11.92C21.95 9.27 20.92 6.78 19.05 4.91C17.18 3.03 14.69 2 12.04 2ZM12.05 20.16C10.57 20.16 9.12 19.76 7.85 19.01L7.55 18.83L4.43 19.65L5.26 16.61L5.06 16.29C4.24 14.99 3.8 13.47 3.8 11.91C3.8 7.37 7.5 3.67 12.05 3.67C14.25 3.67 16.31 4.53 17.87 6.09C19.42 7.65 20.28 9.72 20.28 11.92C20.28 16.46 16.58 20.16 12.05 20.16ZM16.57 14.39C16.32 14.26 15.1 13.66 14.87 13.58C14.65 13.5 14.49 13.46 14.32 13.71C14.16 13.96 13.7 14.49 13.55 14.66C13.41 14.82 13.26 14.85 13.01 14.72C12.77 14.6 11.98 14.34 11.05 13.51C10.33 12.86 9.84 12.06 9.69 11.82C9.55 11.57 9.68 11.43 9.8 11.31C9.91 11.2 10.05 11.02 10.17 10.88C10.29 10.74 10.33 10.63 10.41 10.47C10.49 10.31 10.45 10.17 10.39 10.05C10.33 9.93 9.84 8.73 9.63 8.23C9.43 7.75 9.23 7.81 9.08 7.81C8.93 7.8 8.77 7.8 8.61 7.8C8.44 7.8 8.17 7.86 7.94 8.11C7.72 8.36 7.08 8.96 7.08 10.18C7.08 11.4 7.97 12.57 8.09 12.74C8.21 12.9 9.83 15.4 12.32 16.47C12.91 16.73 13.37 16.88 13.73 16.99C14.32 17.18 14.86 17.15 15.29 17.09C15.77 17.02 16.76 16.49 16.97 15.91C17.17 15.34 17.17 14.85 17.11 14.75C17.05 14.65 16.82 14.51 16.57 14.39Z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="font-display text-xl">WhatsApp Support</h2>
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">
                      Fastest Response
                    </span>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  Connect instantly with our floral specialists for custom arrangement
                  consultations, order tracking, and live help.
                </p>
                <a
                  href="https://wa.me/919344580982?text=Hello%20Aster%20Decorz!%20%F0%9F%91%8B%20I%20would%20like%20to%20get%20in%20touch%20for%20order%20assistance."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex min-h-12 w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-[#25D366] hover:bg-[#20ba5a] px-8 text-xs font-bold uppercase tracking-wider text-white transition-colors shadow-md"
                >
                  <span>Chat on WhatsApp (+91 9344580982)</span>
                </a>
              </div>
            </Reveal>

            {/* Email Card */}
            <Reveal delay={80}>
              <div className="border bg-card p-7 rounded-2xl">
                <Mail className="h-6 w-6 text-primary" strokeWidth={1.25} />
                <h2 className="mt-4 font-display text-xl">Email us</h2>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Include your order number if your question is about an existing order — it saves a
                  round trip.
                </p>
                <a
                  href={`mailto:${siteConfig.contact.email}`}
                  className="mt-6 inline-flex min-h-12 items-center bg-primary px-8 text-xs tracking-[0.2em] text-primary-foreground uppercase transition-opacity hover:opacity-90 rounded-xl font-medium"
                >
                  {siteConfig.contact.email}
                </a>
              </div>
            </Reveal>
          </div>

          <div className="space-y-8 lg:col-span-5 lg:col-start-8">
            <Reveal delay={100}>
              <div className="flex gap-4">
                <MapPin
                  className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground"
                  strokeWidth={1.25}
                />
                <div>
                  <h2 className="font-display text-lg">Where we are</h2>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {siteConfig.contact.address}. Pieces are assembled by hand in our workshop and
                    shipped across India.
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={160}>
              <div className="flex gap-4">
                <Clock
                  className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground"
                  strokeWidth={1.25}
                />
                <div>
                  <h2 className="font-display text-lg">Before you write</h2>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    Delivery charges, GST and the {siteConfig.shipping.returnDays}-day return window
                    are set out on the{" "}
                    <Link to="/shipping" className="text-primary underline underline-offset-4">
                      shipping page
                    </Link>
                    , and the{" "}
                    <Link to="/faq" className="text-primary underline underline-offset-4">
                      FAQs
                    </Link>{" "}
                    cover sizes, pots and upkeep.
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={220}>
              <div>
                <h2 className="font-display text-lg">Elsewhere</h2>
                <ul className="mt-3 space-y-1.5 text-sm">
                  {Object.entries(siteConfig.social).map(([label, href]) => (
                    <li key={label}>
                      <a
                        href={href}
                        rel="noopener noreferrer"
                        target="_blank"
                        className="capitalize text-muted-foreground transition-colors hover:text-primary"
                      >
                        {label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}
